import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as Yup from 'yup';
import { useParams, useNavigate } from 'react-router-dom';
import { Form } from '@unform/web';
import Select from 'react-select';

import Api, { exceptionNotificationAPI } from '../../services/api';
import getValidationErrors from '../../utils/getValidationErrors';
import { formSelectStyles, formSelectControlFullWidth } from '../../components/FormInput/formSelectStyles';
import { Container } from '../../components/FormCrud/styles';
import Button from '../../components/Button';
import FormInput from '../../components/FormInput';
import CancelButton from '../../components/CancelButton';

/** Sempre incluídas no save (ativo / acesso à tag); não aparecem no select. */
const IMPLICIT_TAG_ROLE_NAMES = ['RoleAdmin', 'RoleApprover - Global', 'RoleReviewer - Global'];

const roleTranslation = {
    APPROVER_LOCAL: 'RoleApprover - Local',
    APPROVER_GLOBAL: 'RoleApprover - Global',
    REVIEWER_LOCAL: 'RoleReviewer - Local',
    REVIEWER_GLOBAL: 'RoleReviewer - Global',
};

const isImplicitTagRoleOption = (opt) =>
    opt != null && IMPLICIT_TAG_ROLE_NAMES.includes(String(opt.label).trim());

const selectStyles = {
    ...formSelectStyles,
    control: (base, state) => ({ ...formSelectControlFullWidth(base, state) }),
};

const TagForm = () => {
    const formRef = useRef(null);
    const navigate = useNavigate();
    const { id } = useParams();

    const [isLoading, setIsLoading] = useState(true);
    const [initialFormFields, setInitialFormFields] = useState(undefined);
    const [data, setData] = useState(null);
    const [isActive, setIsActive] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [roleOptions, setRoleOptions] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const implicitTagRolesRef = useRef([]);

    const fields = [
        {
            label: 'Tag Description',
            name: 'tagDescription',
            type: 'input',
            required: true,
        },
        {
            label: 'Tag Color',
            name: 'tagColor',
            type: 'select',
            required: true,
            options: [
                { value: '#102a63', label: <><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#102a63', marginRight: '8px', borderRadius: '2px' }}></span> Azul Escuro</> },
                { value: '#004f9f', label: <><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#004f9f', marginRight: '8px', borderRadius: '2px' }}></span> Azul Médio</> },
                { value: '#00b1eb', label: <><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#00b1eb', marginRight: '8px', borderRadius: '2px' }}></span> Azul Claro</> },
                { value: '#ef7d00', label: <><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#ef7d00', marginRight: '8px', borderRadius: '2px' }}></span> Laranja</> },
                { value: '#50af47', label: <><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#50af47', marginRight: '8px', borderRadius: '2px' }}></span> Verde</> },
                { value: '#fbba00', label: <><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#fbba00', marginRight: '8px', borderRadius: '2px' }}></span> Amarelo</> },
                { value: '#5a328a', label: <><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#5a328a', marginRight: '8px', borderRadius: '2px' }}></span> Roxo</> },
                { value: '#f93b94', label: <><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#f93b94', marginRight: '8px', borderRadius: '2px' }}></span> Rosa</> },
            ],
        },
    ];

    const loadInitial = useCallback(async () => {
        setIsLoading(true);
        setInitialFormFields(undefined);
        try {
            const rolesRes = await Api.get('tag/getAllRoles');
            const opts = (rolesRes.data?.response ?? []).map((item) => ({
                value: String(item.id || item.name),
                label: item.name || 'Unnamed Role',
            }));

            const implicit = opts.filter(isImplicitTagRoleOption);
            implicitTagRolesRef.current = implicit;
            setRoleOptions(opts.filter((o) => !isImplicitTagRoleOption(o)));

            if (id != null) {
                const tagRes = await Api.get(`tag/get?id=${id}`);
                const response = tagRes.data?.response;
                if (response) {
                    setInitialFormFields({
                        tagDescription: response.tagDescription ?? '',
                        tagColor: response.tagColor ?? '',
                    });
                    setIsActive(!!response.isActive);

                    const tagRoles = response.tagRoles ?? [];
                    const rolesFromDB = tagRoles
                        .map((tr) => {
                            const rawID = String(tr.roleID || tr.roleName);
                            const friendlyLabel =
                                roleTranslation[rawID] ||
                                opts.find((o) => o.value === rawID)?.label ||
                                rawID;
                            return {
                                value: rawID,
                                label: friendlyLabel,
                            };
                        })
                        .filter((r) => !isImplicitTagRoleOption(r));

                    setSelectedRoles(rolesFromDB);
                }
            } else {
                setSelectedRoles([]);
                setInitialFormFields({ tagDescription: '', tagColor: '' });
                setIsActive(true);
            }
        } catch (e) {
            exceptionNotificationAPI(e);
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    const handleRoleChange = (newValue) => {
        setSelectedRoles(newValue ?? []);
    };

    useEffect(() => {
        loadInitial();
    }, [loadInitial]);

    useEffect(() => {
        if (isLoading || initialFormFields === undefined) return;
        formRef.current?.setData(initialFormFields);
    }, [isLoading, initialFormFields]);

    const handleSubmit = async (formData) => {
        try {
            setFieldErrors({});

            const validationShape = {};
            fields.forEach((f) => {
                if (f.required === true && f.type !== 'check') {
                    validationShape[f.name] = Yup.string().required(`${f.label} is required`);
                }
            });

            const schema = Yup.object().shape(validationShape);
            await schema.validate(formData, { abortEarly: false });

            const implicitRows = (implicitTagRolesRef.current ?? []).map((r) => ({
                roleID: r.value,
                roleName: String(r.label),
            }));
            const userRows = selectedRoles.map((r) => ({
                roleID: r.value,
                roleName: typeof r.label === 'string' ? r.label : String(r.label),
            }));
            const seen = new Set();
            const tagRoles = [...implicitRows, ...userRows].filter((row) => {
                const key = String(row.roleID).toLowerCase();
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });

            const postObj = {
                ...(id != null ? { tagID: Number(id) } : {}),
                tagDescription: formData.tagDescription,
                tagColor: formData.tagColor,
                isActive,
                tagRoles,
            };
            await Api.post(`tag/${id != null ? 'edit' : 'new'}`, postObj);
            navigate('/tag');
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                const errors = getValidationErrors(error);
                setFieldErrors(errors);
                formRef.current?.setErrors(errors);
                return;
            }
            exceptionNotificationAPI(error);
        }
    };

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <Container className="container">
            <div>
                <h3>{id != null ? 'Edit' : 'New'} Tag</h3>
                <Form onSubmit={handleSubmit} ref={formRef}>
                    <table className="table table-striped">
                        <tbody>
                            {fields.map((f, i) => (
                                <tr key={i + 'Tr'} id={f.name + 'Tr'}>
                                    <td width="25%">
                                        <label>
                                            {f.label} {f.required && <span className="required">*</span>}
                                        </label>
                                    </td>
                                    <td width="75%">
                                        <FormInput
                                            className="form-control"
                                            name={f.name}
                                            id={f.name}
                                            inputType={f.type}
                                            options={f.options}
                                            value={data?.[f.name]}
                                            onChange={(a) => {
                                                if (data != null && a?.target != null) {
                                                    setData((prev) => (prev ? { ...prev, [f.name]: a.target.value } : prev));
                                                }
                                            }}
                                        />
                                    </td>
                                </tr>
                            ))}
                            <tr id="tagRolesTr">
                                <td width="25%">
                                <label>
                                    Roles
                                </label>
                                </td>
                                <td width="75%">
                                    <Select
                                        inputId="tagRoles"
                                        isMulti
                                        isSearchable
                                        closeMenuOnSelect={false}
                                        options={roleOptions}
                                        value={selectedRoles}
                                        onChange={handleRoleChange}
                                        placeholder="Select one or more roles"
                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                        menuPosition="fixed"
                                        styles={selectStyles}
                                        classNamePrefix="tagRoles"
                                    />
                                </td>
                            </tr>
                            <tr id="isActiveTr">
                                <td width="25%">
                                    <label htmlFor="isActive">
                                        Is Active <span className="required">*</span>
                                    </label>
                                </td>
                                <td width="75%">
                                    <input
                                        type="checkbox"
                                        id="isActive"
                                        name="isActive"
                                        className="form-check-input"
                                        checked={isActive}
                                        onChange={(e) => setIsActive(e.target.checked)}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div className="d-flex flex-row mt-2">
                        <CancelButton />
                        <Button type="submit">Save</Button>
                    </div>
                </Form>
            </div>
        </Container>
    );
};

export default TagForm;
